import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { useLocalizedStringsContext } from '../../../context/LocalizedStringsContext';
import getFormattedPricing from '../utils';
import {
  SummaryWrapper,
  SummaryGroupHeading,
  SummaryTable,
  SummaryThead,
  SummaryTbody,
  SummaryTr,
  SummaryTh,
  SummaryTd,
  SummaryTotalTr,
  SummaryTotalTd,
  RemoveButton
} from './css';

/**
 * Summary of the products a rep has completed in the popup, rendered directly
 * below the `IoTPopupButton`. Products are grouped into a separate table per
 * source popup table (`sourceTable`), each with its own heading. Each row
 * exposes an accessible remove control that withdraws the product (recomputing
 * the order totals). When no products have been added yet, nothing is rendered.
 */
const IotSelectedSummaryTable = ({ products, onRemove }) => {
  const localizedStrings = useLocalizedStringsContext();
  const strings = _.get(localizedStrings, 'iotDynamicTable.iotSelectedSummary', {});
  const removeLabel = _.get(strings, 'removeLabel', 'Remove');
  const totalLabel = _.get(strings, 'totalLabel', 'Total');

  const list = Array.isArray(products) ? products : [];

  if (list.length === 0) {
    return null;
  }

  const groups = Object.entries(
    list.reduce((acc, product) => {
      const groupName =
        _.get(product, 'sourceTable', '') || _.get(product, 'rateplanType', '');
      if (!acc[groupName]) acc[groupName] = [];
      acc[groupName].push(product);
      return acc;
    }, {})
  );

  return (
    <>
      {groups.map(([groupName, groupProducts]) => {
        const groupTitle =
          _.get(groupProducts, '[0].sourceTableTitle', '') || groupName;
        const monthlyTotal = groupProducts.reduce(
          (sum, product) => sum + (_.get(product, 'monthly', 0) || 0),
          0
        );
        const oneTimeTotal = groupProducts.reduce(
          (sum, product) => sum + (_.get(product, 'oneTime', 0) || 0),
          0
        );
        // Only show the Term column when at least one product in this group has
        // a term; term-less tables omit it entirely.
        const hasTerm = groupProducts.some(product => _.get(product, 'term'));
        return (
          <SummaryWrapper key={groupName || 'ungrouped'}>
            {groupTitle && (
              <SummaryGroupHeading>{groupTitle}</SummaryGroupHeading>
            )}
            <SummaryTable>
              <SummaryThead>
                <SummaryTr>
                  <SummaryTh scope="col">{_.get(strings, 'productColumn', 'Product')}</SummaryTh>
                  {hasTerm && (
                    <SummaryTh scope="col">{_.get(strings, 'termColumn', 'Term')}</SummaryTh>
                  )}
                  <SummaryTh scope="col">{_.get(strings, 'quantityColumn', 'Quantity')}</SummaryTh>
                  <SummaryTh scope="col">{_.get(strings, 'monthlyColumn', 'Monthly')}</SummaryTh>
                  <SummaryTh scope="col">{_.get(strings, 'oneTimeColumn', 'One-time')}</SummaryTh>
                  <SummaryTh scope="col" aria-label={removeLabel} />
                </SummaryTr>
              </SummaryThead>
              <SummaryTbody>
                {groupProducts.map(product => (
                  <SummaryTr key={product.key}>
                    <SummaryTd>{_.get(product, 'label', '')}</SummaryTd>
                    {hasTerm && (
                      <SummaryTd>{_.get(product, 'term', '') || '—'}</SummaryTd>
                    )}
                    <SummaryTd>{_.get(product, 'quantity', 0)}</SummaryTd>
                    <SummaryTd>{getFormattedPricing(_.get(product, 'monthly', 0))}</SummaryTd>
                    <SummaryTd>{getFormattedPricing(_.get(product, 'oneTime', 0))}</SummaryTd>
                    <SummaryTd>
                      <RemoveButton
                        type="button"
                        aria-label={`${removeLabel} ${_.get(product, 'label', '')}`}
                        onClick={() => onRemove(product.key)}
                      >
                        {removeLabel}
                      </RemoveButton>
                    </SummaryTd>
                  </SummaryTr>
                ))}
                <SummaryTotalTr>
                  <SummaryTotalTd colSpan={hasTerm ? 3 : 2}>
                    {totalLabel}
                  </SummaryTotalTd>
                  <SummaryTotalTd>{getFormattedPricing(monthlyTotal)}</SummaryTotalTd>
                  <SummaryTotalTd>{getFormattedPricing(oneTimeTotal)}</SummaryTotalTd>
                  <SummaryTotalTd />
                </SummaryTotalTr>
              </SummaryTbody>
            </SummaryTable>
          </SummaryWrapper>
        );
      })}
    </>
  );
};

IotSelectedSummaryTable.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({})),
  onRemove: PropTypes.func
};

IotSelectedSummaryTable.defaultProps = {
  products: [],
  onRemove: () => {}
};

export default IotSelectedSummaryTable;
